import { fillPosNegGaps } from 'utils/components/TimeSeriesChart/posNegGradientLine';
import { TimeSeriesChartDataItem } from 'utils/components/TimeSeriesChart/TimeSeriesChartData';
import { FillsGraphData, OrderFill } from 'widgets/fillsGraph/fillsGraphData';

export interface FillsGraphPlChartItem extends TimeSeriesChartDataItem {
    time: number;
    realizedPnl?: number | undefined;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'pos-realizedPnl'?: number | undefined;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'neg-realizedPnl'?: number | undefined;
    totalPnl?: number | undefined;
    unrealizedPnl?: number | undefined;
    allInterpolated?: boolean;
}

export function getFillsGraphPlData(
    fillsGraphData: FillsGraphData
): FillsGraphPlChartItem[] {
    const { data: timeSeries } = fillsGraphData;
    return getFillsGraphPlDataFromTimeSeries(timeSeries);
}

export function getFillsGraphPlDataFromTimeSeries(
    timeSeries: OrderFill[]
): FillsGraphPlChartItem[] {
    if (timeSeries.length === 0) {
        return [];
    }

    const mappedTimeSeries: FillsGraphPlChartItem[] =
        timeSeries.map(mapTimeSeriesItem);

    const filledData = fillPosNegGaps(mappedTimeSeries, 'realizedPnl', [
        'totalPnl',
        'unrealizedPnl',
    ]);

    appendZeroRealized(filledData);

    return removeTrailingPnls(filledData);
}

function mapTimeSeriesItem(item: OrderFill): FillsGraphPlChartItem {
    const ret: FillsGraphPlChartItem = {
        time: item.time,
    };

    const { realizedPnl, totalPnl, unrealizedPnl } = item;

    ret.allInterpolated =
        realizedPnl?.isInterpolated &&
        totalPnl?.isInterpolated &&
        unrealizedPnl?.isInterpolated;

    ret.realizedPnl = realizedPnl?.value;
    ret.unrealizedPnl = unrealizedPnl?.value;
    ret.totalPnl = totalPnl?.value;

    ret['pos-realizedPnl'] =
        ret.realizedPnl !== undefined && ret.realizedPnl > 0
            ? ret.realizedPnl
            : undefined;
    ret['neg-realizedPnl'] =
        ret.realizedPnl !== undefined && ret.realizedPnl < 0
            ? ret.realizedPnl
            : undefined;

    return ret;
}

function removeTrailingPnls(
    items: FillsGraphPlChartItem[]
): FillsGraphPlChartItem[] {
    const withoutUndefined = items.filter(
        (i) =>
            i.realizedPnl !== undefined ||
            i.totalPnl !== undefined ||
            i.unrealizedPnl !== undefined
    );

    const lastWithNotInterpolatedValue = Math.max(
        ...withoutUndefined.filter((i) => !i.allInterpolated).map((i) => i.time)
    );
    return withoutUndefined.filter(
        (i) => i.time <= lastWithNotInterpolatedValue
    );
}

function appendZeroRealized(items: FillsGraphPlChartItem[]) {
    const haveRealized = items.filter((i) => i.realizedPnl !== undefined);

    if (!haveRealized) {
        return;
    }

    let inPnlRange = false;

    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (
            item.realizedPnl !== undefined ||
            item.totalPnl !== undefined ||
            item.unrealizedPnl !== undefined
        ) {
            inPnlRange = true;
        }

        if (!inPnlRange) {
            // Only fill zeros when we have some PnL values
            continue;
        }
        if (item.realizedPnl) {
            // Found the first one with realised so exit
            return;
        }
        item.realizedPnl = 0;
        item['pos-realizedPnl'] = 0;
    }
}
